/**
 * The code is based on 'ng2-file-upload' library (https://github.com/valor-software/ng2-file-upload)
 *
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 Dmitriy Shekhovtsov <valorkin@gmail.com>
 * Copyright (c) 2015 Valor Software
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { FileLikeObject } from "./file-like-object";
import { FileUploader, FileUploaderOptions, Headers, ParsedResponseHeaders } from "./file-uploader";

export class FileItem {
    file: FileLikeObject;
    _file: File;
    alias?: string;
    url = "/";
    method?: string;
    headers: Headers[] = [];
    withCredentials = true;
    isReady = false;
    isUploading = false;
    isUploaded = false;
    isSuccess = false;
    isCancel = false;
    isError = false;
    progress = 0;
    index?: number | undefined;
    _xhr?: XMLHttpRequest;

    protected uploader: FileUploader;
    protected some: File;
    protected options: FileUploaderOptions;

    constructor(uploader: FileUploader, some: File, options: FileUploaderOptions) {
        this.uploader = uploader;
        this.some = some;
        this.options = options;
        this.file = new FileLikeObject(some);
        this._file = some;
        if (uploader.options) {
            this.method = uploader.options.method || "POST";
            this.alias = uploader.options.itemAlias || "file";
        }
        this.url = uploader.options.url;
    }

    upload(): void {
        try {
            this.uploader.uploadItem(this);
        } catch (e) {
            this.uploader._onCompleteItem(this, "", 0, {});
            this.uploader._onErrorItem(this, "", 0, {});
        }
    }

    cancel(): void {
        this.uploader.cancelItem(this);
    }

    remove(): void {
        this.uploader.removeFromQueue(this);
    }

    onBeforeUpload(): undefined {
        return undefined;
    }

    onBuildForm(form: FormData): { form: FormData } {
        return { form };
    }

    onProgress(progress: number): { progress: number } {
        return { progress };
    }

    onSuccess(
        response: string,
        status: number,
        headers: ParsedResponseHeaders
    ): { response: string; status: number; headers: ParsedResponseHeaders } {
        return { response, status, headers };
    }

    onError(
        response: string,
        status: number,
        headers: ParsedResponseHeaders
    ): { response: string; status: number; headers: ParsedResponseHeaders } {
        return { response, status, headers };
    }

    onCancel(
        response: string,
        status: number,
        headers: ParsedResponseHeaders
    ): { response: string; status: number; headers: ParsedResponseHeaders } {
        return { response, status, headers };
    }

    onComplete(
        response: string,
        status: number,
        headers: ParsedResponseHeaders
    ): { response: string; status: number; headers: ParsedResponseHeaders } {
        return { response, status, headers };
    }

    _onBeforeUpload(): void {
        this.isReady = true;
        this.isUploading = true;
        this.isUploaded = false;
        this.isSuccess = false;
        this.isCancel = false;
        this.isError = false;
        this.progress = 0;
        this.onBeforeUpload();
    }

    _onBuildForm(form: FormData): void {
        this.onBuildForm(form);
    }

    _onProgress(progress: number): void {
        this.progress = progress;
        this.onProgress(progress);
    }

    _onSuccess(response: string, status: number, headers: ParsedResponseHeaders): void {
        this.isReady = false;
        this.isUploading = false;
        this.isUploaded = true;
        this.isSuccess = true;
        this.isCancel = false;
        this.isError = false;
        this.progress = 100;
        this.index = undefined;
        this.onSuccess(response, status, headers);
    }

    _onError(response: string, status: number, headers: ParsedResponseHeaders): void {
        this.isReady = false;
        this.isUploading = false;
        this.isUploaded = true;
        this.isSuccess = false;
        this.isCancel = false;
        this.isError = true;
        this.progress = 0;
        this.index = undefined;
        this.onError(response, status, headers);
    }

    _onCancel(response: string, status: number, headers: ParsedResponseHeaders): void {
        this.isReady = false;
        this.isUploading = false;
        this.isUploaded = false;
        this.isSuccess = false;
        this.isCancel = true;
        this.isError = false;
        this.progress = 0;
        this.index = undefined;
        this.onCancel(response, status, headers);
    }

    _onComplete(response: string, status: number, headers: ParsedResponseHeaders): void {
        this.onComplete(response, status, headers);

        if (this.uploader.options.removeAfterUpload) {
            this.remove();
        }
    }

    _prepareToUploading(): void {
        this.index = this.index || ++this.uploader._nextIndex;
        this.isReady = true;
    }
}
